import { useAuthRoles } from '_lib/hooks/useAuthRoles';
import { reportException } from '_lib/utils/sentryService';

import {
  CreateResource,
  DeleteResource,
  GetByIdResource,
  ListResource,
  PutHook,
  PutResource,
  ReactivateResource,
  ResourceId,
  UnpagedListHookResult,
  UnpagedListOptions,
  UnpagedListResource,
  UseCustomMutationOptions,
  createApiResource,
  useCustomMutation
} from '../apiResourceFactory';
import { API } from '../apiService';
import {
  UserDetailDTO,
  UserListDTO,
  IsActiveEnum,
  UserQueryParams,
  ChangePasswordDTO,
  UserUpdateDTO,
  UserCreateDTO
} from '../apiTypes.shim';

import type { SetRequired } from 'type-fest';

type AllCallCenterUsersParams = Omit<SetRequired<UserQueryParams, 'callCenterId'>, 'isActive'>;

export interface UserResource
  extends GetByIdResource<UserDetailDTO>,
    PutResource<UserUpdateDTO, UserDetailDTO>,
    CreateResource<UserCreateDTO, UserDetailDTO>,
    ListResource<UserListDTO, UserQueryParams>,
    UnpagedListResource<UserListDTO, UserQueryParams>,
    DeleteResource,
    ReactivateResource<UserDetailDTO> {
  useAllCallCenterUsersCached: (
    options: UnpagedListOptions<AllCallCenterUsersParams, { requireParams: true }>
  ) => UnpagedListHookResult<UserListDTO>;
  useChangePassword: PutHook<ChangePasswordDTO, string>;
}

export const userResourceUri = `user`;

export const createUserResource = (): UserResource => {
  const {
    useGetById,
    getById,
    list,
    listQueryKey,
    useList,
    useUnpagedList,
    unpagedList,
    unpagedListQueryKey,
    getByIdQueryKey,
    usePut,
    put,
    useCreate,
    create,
    useDelete,
    deleteItem,
    useReactivate,
    reactivate
  } = createApiResource(API, userResourceUri);

  type ChangePasswordVariables = { id: ResourceId; data: ChangePasswordDTO };
  const useChangePassword = (mutationConfig?: UseCustomMutationOptions<string, unknown, ChangePasswordVariables>) => {
    const context = useCustomMutation<string, unknown, ChangePasswordVariables>({
      mutationFn: async ({ id, data }) => {
        const response = await API.put(`${userResourceUri}/${id}/change-password`, data);
        return typeof response.headers.getAuthorization === 'function' ? `${response.headers.getAuthorization()}` : '';
      },
      onError: (error) => reportException(error),
      ...mutationConfig
    });

    return { ...context, put: context.mutateAsync };
  };

  return {
    useAllCallCenterUsersCached: (options) => {
      const authRoles = useAuthRoles();

      return useUnpagedList(
        { ...options, params: { ...options.params, paging: false, isActive: IsActiveEnum.Any } },
        {
          enabled: !authRoles.isAccountAdmin && !authRoles.isAccountUser,
          refetchOnMount: false
        }
      );
    },
    list,
    listQueryKey,
    useList,
    useUnpagedList,
    unpagedList,
    unpagedListQueryKey,
    useChangePassword,
    useGetById,
    getById,
    getByIdQueryKey,
    usePut,
    put,
    useCreate,
    create,
    useDelete,
    deleteItem,
    useReactivate,
    reactivate
  };
};
